/* eslint-disable max-lines -- the picker, single-app, and outcome rows share one connect-wait outcome model */
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { ConnectedAppIcon } from '@heidi/integrations/connected-apps';
import { cn } from '@heidi/utils';
import { Loader2 } from 'lucide-react';
import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { FormattedMessage } from 'react-intl';

import { getWorkHostPort } from '../../work-home/work-home-ports';
import { resolveConnectAppOptions } from './connectAppChips.defaults';

import type { ApprovalStatus, ChatItem } from '../../../contracts/harness.types';
import type {
  ConnectAppOption,
  ConnectAppStack,
} from '../../../contracts/projects/integrations/connectAppChips.defaults';
import { useContinueAfterConnect } from '../../../data/projects/components/continueAfterConnect';
import type { ConnectRequiredPayload } from '../../../data/projects/integrations/connectRequired';
import { InlineApprovalBanner } from './inline-approval/inline-approval-banner';

export interface ConnectRequiredBlockProps {
  payload: ConnectRequiredPayload;
  /** Fallback error copy when no connectable app resolves. */
  error?: string;
  className?: string;
  /** Stable tool-row id so Connected/Cancelled/Failed survives remount. */
  toolCallId?: string;
  /** Timeline items — used to restore Connected after a reload of old turns. */
  items?: readonly ChatItem[];
  /** When set, Approve/Deny resolves this connect wait instead of a new user turn. */
  approvalId?: string;
  onResolve?: (approvalId: string, verdict: 'approve' | 'deny') => Promise<void>;
  approvalStatus?: ApprovalStatus;
}

/** One app's connect button, as the host renders it for its stack. */
export interface ConnectRequiredButtonArgs {
  option: ConnectAppOption;
  stack: ConnectAppStack;
  onConnected: (connector: string) => void;
  onFailed: () => void;
  onBusyChange: (busy: boolean) => void;
  children: ReactNode;
}

/**
 * What the app supplies: the connectable apps (resolved from the integrations
 * catalogs), the stack's OAuth button, and the app's brand mark.
 */
export interface ConnectRequiredBlockHost {
  /** The payload's connectable apps for its stack, in catalog order. */
  options: readonly ConnectAppOption[];
  renderConnectButton: (args: ConnectRequiredButtonArgs) => ReactNode;
  renderAppIcon: (icon: { id: string; label?: string }) => ReactNode;
}

const MAX_CONNECT_ROWS = 4;

const CONNECT_CONTINUE_PREFIX = "I've connected ";

type ConnectOutcome = 'connected' | 'cancelled' | 'failed';

const outcomeCopy = {
  connected: <FormattedMessage defaultMessage="Connected" id="IvjoDSPGEQ" />,
  cancelled: <FormattedMessage defaultMessage="Cancelled" id="3wsVWFbC1x" />,
  failed: <FormattedMessage defaultMessage="Failed" id="vXCeIi67rj" />,
} as const;

const outcomesByToolCallId = new Map<string, ConnectOutcome>();

const outcomeFromTimeline = (
  items: readonly ChatItem[] | undefined,
  toolCallId: string | undefined
): 'connected' | null => {
  if (!items?.length || !toolCallId) return null;
  const idx = items.findIndex((item) => item.id === toolCallId);
  if (idx < 0) return null;
  for (let i = idx + 1; i < items.length; i += 1) {
    const next = items[i];
    if (next?.kind !== 'user_message') continue;
    if (next.text.startsWith(CONNECT_CONTINUE_PREFIX)) {
      return 'connected';
    }
  }
  return null;
};

const outcomeFromApproval = (
  status: ApprovalStatus | undefined
): ConnectOutcome | null => {
  if (status === 'approved') return 'connected';
  if (status === 'denied' || status === 'timed_out') return 'cancelled';
  return null;
};

const resolveInitialOutcome = (
  toolCallId: string | undefined,
  items: readonly ChatItem[] | undefined,
  approvalStatus: ApprovalStatus | undefined
): ConnectOutcome | null => {
  const fromApproval = outcomeFromApproval(approvalStatus);
  if (fromApproval) return fromApproval;
  if (toolCallId) {
    const remembered = outcomesByToolCallId.get(toolCallId);
    if (remembered) return remembered;
  }
  return outcomeFromTimeline(items, toolCallId);
};

function ConnectToAppTitle({ app }: { app: string }) {
  return (
    <FormattedMessage
      defaultMessage="Heidi wants to connect to {app}"
      id="qEXW2ZSgIN"
      values={{ app }}
    />
  );
}

function ConnectToKindTitle({ kind }: { kind: string }) {
  switch (kind) {
    case 'calendar':
      return (
        <FormattedMessage
          defaultMessage="Heidi wants to connect to your calendar"
          id="V+48QKcIpe"
        />
      );
    case 'mail':
      return (
        <FormattedMessage
          defaultMessage="Heidi wants to connect to your email"
          id="dkgwcm2Yxw"
        />
      );
    case 'files':
      return (
        <FormattedMessage
          defaultMessage="Heidi wants to connect to your files"
          id="10d9c66WY7"
        />
      );
    default:
      return (
        <FormattedMessage
          defaultMessage="Heidi wants to connect an app"
          id="7pw5l8QOcK"
        />
      );
  }
}

function ConnectRequiredOutcomeRow({
  title,
  appId,
  outcome,
  renderAppIcon,
}: {
  title: ReactNode;
  appId?: string;
  outcome: ConnectOutcome;
  renderAppIcon: ConnectRequiredBlockHost['renderAppIcon'];
}) {
  return (
    <InlineApprovalBanner
      data-testid="connect-required-outcome-row"
      mark={appId ? renderAppIcon({ id: appId }) : undefined}
      title={title}
      actions={
        <TypographyCaption
          className="text-text-secondary"
          data-testid="connect-required-outcome"
        >
          {outcomeCopy[outcome]}
        </TypographyCaption>
      }
    />
  );
}

function ConnectRequiredWaiting() {
  return (
    <div
      className="flex items-center gap-2 text-text-secondary"
      data-testid="connect-required-waiting"
    >
      <Loader2 className="size-3.5 animate-spin" />
      <TypographyCaption>
        <FormattedMessage defaultMessage="Waiting on setup" id="6B9cu3acvy" />
      </TypographyCaption>
    </div>
  );
}

interface ConnectRowCallbacks {
  stack: ConnectAppStack;
  onConnected: (connector: string) => void;
  onDeny: () => void;
  onFailed: () => void;
  renderConnectButton: ConnectRequiredBlockHost['renderConnectButton'];
}

function DenyButton({ onDeny }: { onDeny: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onDeny}
      data-testid="connect-required-deny"
    >
      <FormattedMessage defaultMessage="Deny" id="htvX+Z11l+" />
    </Button>
  );
}

/**
 * One card for a connect wait that offers several apps (e.g. "connect my
 * calendar" → Outlook or Google Calendar). Picking an app starts its OAuth;
 * a single Deny cancels the wait.
 */
function ConnectRequiredPickerRow({
  kind,
  options,
  onDeny,
  renderConnectButton,
  ...callbacks
}: ConnectRowCallbacks & { kind: string; options: readonly ConnectAppOption[] }) {
  const [waiting, setWaiting] = useState(false);

  return (
    <InlineApprovalBanner
      data-testid="connect-required-picker"
      title={<ConnectToKindTitle kind={kind} />}
      actions={
        waiting ? (
          <ConnectRequiredWaiting />
        ) : (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <DenyButton onDeny={onDeny} />
            {options.map((option) => (
              <Fragment key={option.id}>
                {renderConnectButton({
                  option,
                  onBusyChange: setWaiting,
                  ...callbacks,
                  children: option.label ?? option.id,
                })}
              </Fragment>
            ))}
          </div>
        )
      }
    />
  );
}

function ConnectRequiredAppRow({
  option,
  onDeny,
  renderConnectButton,
  renderAppIcon,
  ...callbacks
}: ConnectRowCallbacks & {
  option: ConnectAppOption;
  renderAppIcon: ConnectRequiredBlockHost['renderAppIcon'];
}) {
  const [waiting, setWaiting] = useState(false);
  const app = option.label ?? option.id;

  return (
    <InlineApprovalBanner
      data-testid={`connect-required-${option.id}`}
      mark={renderAppIcon({ id: option.id, label: app })}
      title={<ConnectToAppTitle app={app} />}
      actions={
        waiting ? (
          <ConnectRequiredWaiting />
        ) : (
          <div className="flex items-center justify-end gap-2">
            <DenyButton onDeny={onDeny} />
            {renderConnectButton({
              option,
              onBusyChange: setWaiting,
              ...callbacks,
              children: <FormattedMessage defaultMessage="Approve" id="WCaf5CZSTt" />,
            })}
          </div>
        )
      }
    />
  );
}

/**
 * Shown when a connected-apps tool needs a live connector.
 *
 * Same slim HITL row as in-chat EHR connect: app logo, "Heidi wants to
 * connect to {app}", Deny + Approve. After OAuth or Deny the actions become
 * Connected / Cancelled / Failed. When ``approvalId`` is set, Approve
 * resolves the harness wait so the same turn continues.
 */
export function ConnectRequiredBlockView({
  payload,
  error,
  className,
  toolCallId,
  items,
  approvalId,
  onResolve,
  approvalStatus,
  options: connectableOptions,
  renderConnectButton,
  renderAppIcon,
}: ConnectRequiredBlockProps & ConnectRequiredBlockHost) {
  const continueAfterConnect = useContinueAfterConnect();
  const resumedRef = useRef(false);
  const [outcome, setOutcome] = useState<ConnectOutcome | null>(() =>
    resolveInitialOutcome(toolCallId, items, approvalStatus)
  );
  const [chosenId, setChosenId] = useState<string | null>(null);

  const remember = useCallback(
    (next: ConnectOutcome) => {
      if (toolCallId) outcomesByToolCallId.set(toolCallId, next);
      setOutcome(next);
    },
    [toolCallId]
  );

  useEffect(() => {
    const fromApproval = outcomeFromApproval(approvalStatus);
    if (fromApproval) {
      remember(fromApproval);
      return;
    }
    if (outcome) return;
    const fromTimeline = outcomeFromTimeline(items, toolCallId);
    if (!fromTimeline) return;
    remember(fromTimeline);
  }, [approvalStatus, items, outcome, remember, toolCallId]);

  const handleConnected = useCallback(
    (connector: string) => {
      if (resumedRef.current) return;
      resumedRef.current = true;
      remember('connected');
      if (approvalId && onResolve) {
        void onResolve(approvalId, 'approve');
        return;
      }
      continueAfterConnect?.(connector);
    },
    [approvalId, continueAfterConnect, onResolve, remember]
  );

  const handleDeny = useCallback(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    remember('cancelled');
    if (approvalId && onResolve) {
      void onResolve(approvalId, 'deny');
    }
  }, [approvalId, onResolve, remember]);

  const handleFailed = useCallback(() => {
    if (outcome) return;
    remember('failed');
  }, [outcome, remember]);

  const stack = payload.stack;
  const options = stack ? connectableOptions.slice(0, MAX_CONNECT_ROWS) : [];
  const singleOption = options.length === 1 ? options[0] : undefined;
  const chosenOption = options.find((option) => option.id === chosenId) ?? singleOption;

  const handleOptionConnected = useCallback(
    (connector: string) => {
      setChosenId(connector);
      handleConnected(connector);
    },
    [handleConnected]
  );

  return (
    <div
      className={cn('flex w-full flex-col gap-2', className)}
      data-testid="connect-required-block"
    >
      {outcome ? (
        <ConnectRequiredOutcomeRow
          title={
            chosenOption ? (
              <ConnectToAppTitle app={chosenOption.label ?? chosenOption.id} />
            ) : (
              <ConnectToKindTitle kind={payload.kind} />
            )
          }
          appId={chosenOption?.id}
          outcome={outcome}
          renderAppIcon={renderAppIcon}
        />
      ) : options.length > 1 && stack ? (
        <ConnectRequiredPickerRow
          kind={payload.kind}
          options={options}
          stack={stack}
          onConnected={handleOptionConnected}
          onDeny={handleDeny}
          onFailed={handleFailed}
          renderConnectButton={renderConnectButton}
        />
      ) : singleOption && stack ? (
        <ConnectRequiredAppRow
          option={singleOption}
          stack={stack}
          onConnected={handleConnected}
          onDeny={handleDeny}
          onFailed={handleFailed}
          renderConnectButton={renderConnectButton}
          renderAppIcon={renderAppIcon}
        />
      ) : (
        <TypographyP2 className="text-text-secondary">
          {error ?? (
            <FormattedMessage
              defaultMessage="Connect an app to continue."
              id="UwzqRWMIwo"
            />
          )}
        </TypographyP2>
      )}
    </div>
  );
}

const renderConnectButton = ({
  option,
  stack,
  onConnected,
  onFailed,
  onBusyChange,
  children,
}: ConnectRequiredButtonArgs) => {
  const { NangoConnectButton, MergeConnectButton } = getWorkHostPort('integrations');
  return stack === 'nango' ? (
    <NangoConnectButton
      allowedIntegrations={[option.id]}
      compact
      testId={`nango-connect-${option.id}`}
      onBusyChange={onBusyChange}
      onConnected={() => onConnected(option.id)}
      onFailed={onFailed}
    >
      {children}
    </NangoConnectButton>
  ) : (
    <MergeConnectButton
      connector={option.id}
      compact
      onBusyChange={onBusyChange}
      onConnected={() => onConnected(option.id)}
      onFailed={onFailed}
    >
      {children}
    </MergeConnectButton>
  );
};

const renderAppIcon = (icon: { id: string; label?: string }) => (
  <ConnectedAppIcon {...icon} />
);

/** Binds the connect card to the integrations side. */
export function ConnectRequiredBlock(props: ConnectRequiredBlockProps) {
  const stack = props.payload.stack;
  return (
    <ConnectRequiredBlockView
      {...props}
      options={stack ? resolveConnectAppOptions(props.payload.connectors, stack) : []}
      renderConnectButton={renderConnectButton}
      renderAppIcon={renderAppIcon}
    />
  );
}
